import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import useProposalCalculator from "./useProposalCalculator";
import { fetchProposalCurrencyList } from "../store/actions/companyActions";
import { changeBucketColorAccordingBackground, getClosestSiblingWithSelector, hexToRGB } from "../_helpers/utils";
import { ITINERARY, PRICING_TABLE } from "../constants/editor";

const useEstimatePreview = ({ docPagesMeta, setDocPagesMeta, enable, companyId }) => {
  const dispatch = useDispatch();
  const [showPricingTableImageCarousel, setShowPricingTableImageCarousel] = useState();
  const [tableConfigData, setTableConfigData] = useState();
  const [discountOptions, setDiscountOptions] = useState();
  const [targetSection, setTargetSection] = useState();
  const [targetSectionIndex, setTargetSectionIndex] = useState();
  const [targetItem, setTargetItem] = useState();
  const [targetItemIndex, setTargetItemIndex] = useState();
  const [activeWidgetId, setActiveWidgetId] = useState();
  const [widgetDelta, setWidgetDelta] = useState();
  const [currencyInfo, setCurrencyInfo] = useState();
  const [updatedTables, setUpdatedTables] = useState([]);
  const [activeWidgetType, setActiveWidgetType] = useState(false);

  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const {
    sections: calculatedSections,
    grandSubtotal,
    oneLineDiscountAmount,
    detailedTaxes,
    grandTotal,
  } = useProposalCalculator(tableConfigData?.sections, discountOptions);

  const handleImageClick = event => {
    const itemImageEl = event.target.closest(".item-image");
    const itineraryImageEl = event.target.closest(".itinerary-image");

    // Clicked outside supported elements
    if (!itemImageEl && !itineraryImageEl) return;

    let items = [];
    let imageArray = [];
    let startingImageIndex = 0;

    if (itemImageEl) {
      setActiveWidgetType(PRICING_TABLE);
      const dataSrc = itemImageEl.getAttribute("data-image-urls");
      if (!dataSrc) return;

      imageArray = dataSrc.split(",");
    }

    if (itineraryImageEl) {
      setActiveWidgetType(ITINERARY);
      const imageListEl = itineraryImageEl.closest(".image-list");
      if (!imageListEl) return;

      const dataSrc = imageListEl.getAttribute("data-image-urls");
      if (!dataSrc) return;

      imageArray = dataSrc.split(",");

      startingImageIndex =
        event.target.classList.contains("counter") || event.target.classList.contains("counter-box")
          ? 4
          : parseInt(itineraryImageEl.getAttribute("data-image-number"), 10) || 0;
    }

    imageArray.forEach((img, index) => {
      items.push({
        src: img,
        altText: `${index + 1}/${imageArray.length}`,
        ...(itineraryImageEl && {
          active: index === startingImageIndex,
        }),
      });
    });

    setShowPricingTableImageCarousel(items);
  };

  const upadteItemTableJson = (tableConfig, sectionIndex, itemType, itemIndex) => {
    const newTableConfig = { ...tableConfig };
    sectionIndex = Number(sectionIndex.slice(1));
    itemIndex = Number(itemIndex.slice(1));

    if (itemType === "checkbox") {
      newTableConfig.sections = [...tableConfig.sections];
      newTableConfig.sections[sectionIndex] = {
        ...newTableConfig.sections[sectionIndex],
        items: [...tableConfig.sections[sectionIndex].items],
      };
      const item = newTableConfig.sections[sectionIndex].items[itemIndex];

      newTableConfig.sections[sectionIndex].items[itemIndex] = {
        ...item,
        active: !item.active,
      };
    } else {
      newTableConfig.sections = [...tableConfig.sections];
      const section = { ...newTableConfig.sections[sectionIndex] };

      // Map over items and update active state
      section.items = section.items.map((item, index) => ({
        ...item,
        active: index === itemIndex,
      }));

      newTableConfig.sections[sectionIndex] = section;
    }
    return newTableConfig;
  };

  const upadteSectionTableJson = (tableConfig, sectionType, sectionIndex) => {
    let newTableConfig;
    sectionIndex = Number(sectionIndex.slice(1));

    if (sectionType === "checkbox") {
      newTableConfig = { ...tableConfig };
      newTableConfig.sections = [...tableConfig.sections];

      const section = {
        ...tableConfig.sections[sectionIndex],
        active: !tableConfig.sections[sectionIndex].active,
      };

      newTableConfig.sections[sectionIndex] = section;
    } else {
      newTableConfig = {
        ...tableConfig,
        sections: tableConfig.sections.map((section, index) => ({
          ...section,
          active: index === sectionIndex, // Only the target index is active
        })),
      };
    }
    return newTableConfig;
  };

  const calculateTotalTime = tableConfigData => {
    let newTableConfig = { ...tableConfigData };
    let minHour = 0;

    newTableConfig.sections?.forEach(section => {
      section.items?.forEach(val => {
        if (val.active && parseFloat(val.data.estimated)) {
          minHour += parseFloat(val.data.estimated);
        }
      });
    });
    const total = Number(minHour.toFixed(1));
    return total;
  };

  const handleOptionClick = event => {
    if (!event.target.closest(".dhp-widgets")) return;
    let curActiveWidgetId = event.target.closest(".dhp-widgets").getAttribute("data-id");
    setActiveWidgetId(curActiveWidgetId);
    setUpdatedTables([...updatedTables, curActiveWidgetId]);
    let tableConfig = event.target.closest(".dhp-widgets").getAttribute("data-table-config");
    tableConfig = JSON.parse(tableConfig);
    let target = getClosestSiblingWithSelector(event.target, "input");
    if (!target) return;
    let targetId = target?.getAttribute("id");
    let updatedTableConfig;
    let sectionIndex;
    let checked;

    // handle click on scope of work item
    if (target.classList.contains("scope-of-work-item-input")) {
      sectionIndex = targetId.split("-")[3];
      let itemIndex = targetId.split("-")[4];
      let itemType = targetId.split("-")[2] === "itemcheckbox" ? "checkbox" : "radio";

      if (itemType === "checkbox") {
        let isChecked = document.getElementById(targetId).checked;
        checked = isChecked;
        if (isChecked) document.getElementById(targetId).removeAttribute("checked");
        else document.getElementById(targetId).setAttribute("checked", "");
        document.getElementById(targetId).checked = !isChecked;
      } else {
        let activeSection = document.getElementById(targetId).closest(".card-body");
        activeSection.querySelectorAll(".scope-of-work-item-input").forEach(element => {
          element.checked = false;
          element.removeAttribute("checked");
        });
        document.getElementById(targetId).checked = true;
        document.getElementById(targetId).setAttribute("checked", "");
      }

      document
        .querySelector(`.preview-page [data-id=${curActiveWidgetId}], .present-page [data-id=${curActiveWidgetId}]`)
        .querySelectorAll(".scope-of-work-item-input")
        .forEach(element => {
          if (!element.checked)
            element
              .closest("ul")
              .querySelector(".item-amount-details .scope-of-work-estimated-time")
              .classList.add("disable");
          else
            element
              .closest("ul")
              .querySelector(".item-amount-details .scope-of-work-estimated-time")
              .classList.remove("disable");
        });

      sectionIndex = Number(sectionIndex.slice(1));
      itemIndex = Number(itemIndex.slice(1));

      updatedTableConfig = {
        ...tableConfig,
        sections: tableConfig.sections.map((section, sIdx) => {
          if (sIdx !== sectionIndex) return section;
          return {
            ...section,
            items: section.items.map((item, iIdx) => {
              if (itemType === "checkbox") {
                return iIdx === itemIndex ? { ...item, active: !checked } : item;
              } else {
                return { ...item, active: iIdx === itemIndex };
              }
            }),
          };
        }),
      };

      if (document.querySelector(`[data-id=${curActiveWidgetId}] .total-estimated-show`)) {
        let total = calculateTotalTime(updatedTableConfig);
        document.querySelector(`[data-id=${curActiveWidgetId}] .total-estimated-show`).innerHTML = `${total} Hours`;
      }
    }
    // handle click on section
    if (target.classList.contains("pricing-table-section-input")) {
      sectionIndex = targetId.split("-")[2];
      let sectionType = targetId.split("-")[1] === "sectioncheckbox" ? "checkbox" : "radio";

      //  update html for section click
      if (sectionType === "checkbox") {
        let isChecked = document.getElementById(targetId).checked;
        if (isChecked) document.getElementById(targetId).removeAttribute("checked");
        else document.getElementById(targetId).setAttribute("checked", "");
        document.getElementById(targetId).checked = !isChecked;
      } else {
        document.querySelectorAll(".pricing-table-section-input").forEach(element => {
          element.checked = false;
          element.removeAttribute("checked");
        });
        document.getElementById(targetId).checked = true;
        document.getElementById(targetId).setAttribute("checked", "");
      }

      // disbale other section amount in html
      document.querySelectorAll(".preview-page .pricing-table-section-input, .present-page .pricing-table-section-input").forEach(element => {
        if (!element.checked) element.closest(".card-header").querySelector(".pricing-amount").classList.add("disable");
        else element.closest(".card-header").querySelector(".pricing-amount").classList.remove("disable");
      });

      //  update json for section click
      updatedTableConfig = upadteSectionTableJson(tableConfig, sectionType, sectionIndex);

      setTargetSection();
      setTargetSectionIndex();
    }
    // handle click on item
    if (target.classList.contains("pricing-table-item-input")) {
      sectionIndex = targetId.split("-")[2];
      let itemIndex = targetId.split("-")[3];
      let itemType = targetId.split("-")[1] === "itemcheckbox" ? "checkbox" : "radio";

      //  update html for item click
      if (itemType === "checkbox") {
        let isChecked = document.getElementById(targetId).checked;
        if (isChecked) document.getElementById(targetId).removeAttribute("checked");
        else document.getElementById(targetId).setAttribute("checked", "");
        document.getElementById(targetId).checked = !isChecked;
      } else {
        let activeSection = document.getElementById(targetId).closest(".card-body");
        activeSection.querySelectorAll(".pricing-table-item-input").forEach(element => {
          element.checked = false;
          element.removeAttribute("checked");
        });
        document.getElementById(targetId).checked = true;
        document.getElementById(targetId).setAttribute("checked", "");
      }

      // disbale other item amount in html
      document.querySelectorAll(".preview-page .pricing-table-item-input, .present-page .pricing-table-item-input").forEach(element => {
        if (!element.checked)
          element.closest("ul").querySelector(".item-amount-details .pricing-amount").classList.add("disable");
        else element.closest("ul").querySelector(".item-amount-details .pricing-amount").classList.remove("disable");
      });

      //  update json for item click
      updatedTableConfig = upadteItemTableJson(tableConfig, sectionIndex, itemType, itemIndex);

      setTargetSection(document.getElementById(targetId).closest(".pricing-table-section"));
      setTargetSectionIndex(Number(sectionIndex.slice(1)));
    }

    setTableConfigData(updatedTableConfig);
    setDiscountOptions({
      lineItemDiscount: tableConfig.lineItemDiscount,
      oneLineDiscount: tableConfig.oneLineDiscount,
      oneLineDiscountValue: tableConfig.oneLineDiscountValue,
      oneLineDiscountType: tableConfig.oneLineDiscountType,
    });
  };

  const formattedCurrency = value => {
    let currentCurrency = currencyInfo.find(
      currency =>
        currency.name ===
        document.querySelector(`[data-id=${activeWidgetId}] .dhp-widget-inner`).getAttribute("data-currency")
    );

    const currencyValue = formatCurrency(
      value,
      getSymbolFromName(currentCurrency?.thousand_seperator),
      getSymbolFromName(currentCurrency?.decimal_seperator),
      currentCurrency?.decimal_places,
      currentCurrency?.number_format
    );
    return `${currentCurrency?.symbol} ${currencyValue}`;
  };

  // To format Currency
  function formatCurrency(
    value,
    thousandsSeparator = ",",
    decimalSeparator = ".",
    decimalPlaces = 2,
    numberFormat = "standard"
  ) {
    let [integer, decimal] = Number(value)?.toFixed(decimalPlaces).split("."); // Split the number into integer and decimal parts

    if (numberFormat === "indian") {
      const lastThree = integer.slice(-3);
      const rest = integer.slice(0, -3);

      integer = rest
        ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, thousandsSeparator) + thousandsSeparator + lastThree
        : lastThree;
    } else {
      integer = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    }

    return decimal ? `${integer}${decimalSeparator}${decimal}` : integer;
  }

  // To convert string into separator
  function getSymbolFromName(name) {
    const nameToSymbolMapping = { comma: ",", period: ".", space: " " };
    return nameToSymbolMapping[name];
  }

  const handleQuantityInc = event => {
    if (!event.target.closest(".dhp-widgets")) return;
    let curActiveWidgetId = event.target.closest(".dhp-widgets").getAttribute("data-id");
    setActiveWidgetId(curActiveWidgetId);
    setUpdatedTables([...updatedTables, curActiveWidgetId]);
    let tableConfig = event.target.closest(".dhp-widgets").getAttribute("data-table-config");
    tableConfig = JSON.parse(tableConfig);
    let itemIndex = Number(event.target.closest(".btn-increment").classList[4].split("-")[1]);
    let sectionIndex = Number(event.target.closest(".pricing-table-section").classList[3].split("-")[2]);
    let maxQuantity = tableConfig.sections[sectionIndex].items[itemIndex].maxQty;
    let quantity = tableConfig.sections[sectionIndex].items[itemIndex].data.quantity;
    let updatedQuantity = quantity + 1 > maxQuantity ? quantity : quantity + 1;

    // disable icon
    if (updatedQuantity === maxQuantity) event.target.closest(".btn-increment").classList.add("disable");
    event.target.closest(".input-group").querySelector(".btn-decrement").classList.remove("disable");

    if (quantity !== updatedQuantity) {
      event.target.closest(".input-group").querySelector(".form-control").innerHTML = updatedQuantity; // update html

      // update json
      const newTableConfig = { ...tableConfig };
      newTableConfig.sections = [...tableConfig.sections];
      newTableConfig.sections[sectionIndex] = {
        ...newTableConfig.sections[sectionIndex],
        items: [...tableConfig.sections[sectionIndex].items],
      };
      const item = newTableConfig.sections[sectionIndex].items[itemIndex];

      // Copy the `data` object and update quantity
      const data = {
        ...item.data,
        quantity: updatedQuantity,
      };

      newTableConfig.sections[sectionIndex].items[itemIndex] = {
        ...item,
        data,
      };

      setTargetSection(event.target.closest(".pricing-table-section"));
      setTargetSectionIndex(sectionIndex);
      setTargetItem(event.target.closest("ul").querySelector(".item-amount-details .pricing-amount"));
      setTargetItemIndex(itemIndex);
      setTableConfigData(newTableConfig);
      setDiscountOptions({
        lineItemDiscount: tableConfig.lineItemDiscount,
        oneLineDiscount: tableConfig.oneLineDiscount,
        oneLineDiscountValue: tableConfig.oneLineDiscountValue,
        oneLineDiscountType: tableConfig.oneLineDiscountType,
      });
    }
  };

  const handleQuantityDec = event => {
    if (!event.target.closest(".dhp-widgets")) return;
    let curActiveWidgetId = event.target.closest(".dhp-widgets").getAttribute("data-id");
    setActiveWidgetId(curActiveWidgetId);
    setUpdatedTables([...updatedTables, curActiveWidgetId]);
    let tableConfig = event.target.closest(".dhp-widgets").getAttribute("data-table-config");
    tableConfig = JSON.parse(tableConfig);
    let itemIndex = Number(event.target.closest(".btn-decrement").classList[4].split("-")[1]);
    let sectionIndex = Number(event.target.closest(".pricing-table-section").classList[3].split("-")[2]);
    let minQuantity = tableConfig.sections[sectionIndex].items[itemIndex].minQty;
    let quantity = tableConfig.sections[sectionIndex].items[itemIndex].data.quantity;
    let updatedQuantity = quantity - 1 < minQuantity ? quantity : quantity - 1;

    // disable icon
    if (updatedQuantity === minQuantity) event.target.closest(".btn-decrement").classList.add("disable");
    event.target.closest(".input-group").querySelector(".btn-increment").classList.remove("disable");

    if (quantity !== updatedQuantity) {
      event.target.closest(".input-group").querySelector(".form-control").innerHTML = updatedQuantity; // update html

      // update json
      const newTableConfig = { ...tableConfig };
      newTableConfig.sections = [...tableConfig.sections];
      newTableConfig.sections[sectionIndex] = {
        ...newTableConfig.sections[sectionIndex],
        items: [...tableConfig.sections[sectionIndex].items],
      };
      const item = newTableConfig.sections[sectionIndex].items[itemIndex];

      // Copy the `data` object and update quantity
      const data = {
        ...item.data,
        quantity: updatedQuantity,
      };

      newTableConfig.sections[sectionIndex].items[itemIndex] = {
        ...item,
        data,
      };

      setTargetSection(event.target.closest(".pricing-table-section"));
      setTargetSectionIndex(sectionIndex);
      setTargetItem(event.target.closest("ul").querySelector(".item-amount-details .pricing-amount"));
      setTargetItemIndex(itemIndex);
      setTableConfigData(newTableConfig);
      setDiscountOptions({
        lineItemDiscount: tableConfig.lineItemDiscount,
        oneLineDiscount: tableConfig.oneLineDiscount,
        oneLineDiscountValue: tableConfig.oneLineDiscountValue,
        oneLineDiscountType: tableConfig.oneLineDiscountType,
      });
    }
  };

  const disableInput = () => {
    document.querySelectorAll(".item-quantity-details")?.forEach(el => {
      let hasSpinner = el.querySelector(".input-spinner .form-control");
      if (hasSpinner) el.innerHTML = hasSpinner.textContent;
    });

    document
      .querySelectorAll(
        ".preview-page .custom-radio input, .preview-page .custom-checkbox input, .present-page .custom-radio input, .present-page .custom-checkbox input"
      )
      .forEach(el => {
        el.setAttribute("disabled", true);
        el.style.pointerEvent = "none";
      });
  };

  const createTaxAreaHtml = taxes => {
    const container = document.getElementById("pricing-table-taxes");
    const totalAreaBackGroundColor =
      document.querySelector(".pricing-table-total").style.backgroundColor === ""
        ? hexToRGB("#4472c4")
        : document.querySelector(".pricing-table-total").style.backgroundColor;
    const backgroundColorRange = changeBucketColorAccordingBackground(totalAreaBackGroundColor);
    const taxColor = backgroundColorRange === "dark" ? "#ffffff" : "#000000";

    // Clear existing content
    container.innerHTML = "";

    taxes.forEach((tax, index) => {
      // Create main div
      const cardHeader = document.createElement("div");
      cardHeader.className = "d-flex justify-content-between align-items-center card-header";

      // Create left h6
      const taxName = document.createElement("h6");
      taxName.className = "mb-0 ms-4";
      taxName.innerHTML = `<!-- -->${tax.name}`;
      taxName.style.color = taxColor;

      // Create right h6 and amount div
      const amountWrapper = document.createElement("h6");
      amountWrapper.className = "mb-0";
      amountWrapper.style.color = taxColor;

      const amountDiv = document.createElement("div");
      amountDiv.className = `tax-amount-${index}`;
      amountDiv.textContent = tax.amount;

      // Nest elements
      amountWrapper.appendChild(amountDiv);
      cardHeader.appendChild(taxName);
      cardHeader.appendChild(amountWrapper);
      container.appendChild(cardHeader);
    });

    // update new height
    const innerEl = document.querySelector(`[data-id="${activeWidgetId}"] .dhp-widget-inner`);
    const outerEl = document.querySelector(`[data-id="${activeWidgetId}"]`);

    innerEl.style.height = "";
    const baseHeight = innerEl.offsetHeight;
    const transform = getComputedStyle(innerEl).transform;
    let scale = 1;

    if (transform && transform !== "none") {
      const match = transform.match(/matrix\(([^,]+),[^,]+,[^,]+,[^,]+,[^,]+,[^,]+\)/);
      if (match) {
        scale = parseFloat(match[1]);
      }
    }

    const scaledHeight = baseHeight * scale; // Calculate visual (scaled) height
    outerEl.style.height = `${scaledHeight}px`; // Apply scaled height to the outer container
    innerEl.style.height = `${baseHeight}px`;
  };

  const createSpinnerNode = () => {
    const spinner = document.createElement("span");
    spinner.className = "spinner-border spinner-border-sm text-dark me-1";
    spinner.setAttribute("role", "status");
    spinner.setAttribute("aria-hidden", "true");
    return spinner;
  };

  const toggleDownloadLoader = (docId, inProgress = true) => {
    const container = document.getElementById(docId);
    if (!container) return;

    // The <a> wrapper that contains the icon
    const actionWrapper = container.querySelector("a");
    if (!actionWrapper) return;

    // Cache original markup ONCE
    if (!actionWrapper.__originalHTML) {
      actionWrapper.__originalHTML = actionWrapper.innerHTML;
    }

    if (inProgress) {
      // Prevent duplicate spinner
      if (actionWrapper.__loading) return;

      actionWrapper.innerHTML = "";
      actionWrapper.appendChild(createSpinnerNode());
      actionWrapper.__loading = true;
    } else {
      actionWrapper.innerHTML = actionWrapper.__originalHTML;
      actionWrapper.__loading = false;
    }
  };

  const downloadDocument = async file => {
    try {
      toggleDownloadLoader(file.id, true);
      const response = await fetch(file.url, { method: "GET" });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (err) {
      toggleDownloadLoader(file.id, false);
      console.error(err);
      alert("Failed to download file");
    } finally {
      toggleDownloadLoader(file.id, false);
    }
  };

  const handleWidgetAction = event => {
    if (!event.target.closest(".dhp-widgets")) return;

    const phoneEl = event.target.closest("[data-phone-href]");
    if (phoneEl) {
      if (isMobile) {
        event.preventDefault();
        event.stopPropagation();
        window.location.href = phoneEl.dataset.phoneHref;
      }
      return;
    }

    const documentItem = event.target.closest(".document-list-item");

    const assetUrl = documentItem?.getAttribute("data-asset-url");
    const fileName = documentItem?.getAttribute("data-file-name");
    const fileId = documentItem?.getAttribute("id");

    const address = event.target.getAttribute("data-address");
    const websiteURL = event.target.getAttribute("data-url");

    if (address) {
      const encodedAddress = encodeURIComponent(address);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (websiteURL) {
      const finalUrl = websiteURL.match(/^https?:/) ? websiteURL : "//" + websiteURL;

      window.open(finalUrl, "_blank", "noopener,noreferrer");
      return;
    }

    if (assetUrl && fileId) {
      downloadDocument({
        url: assetUrl,
        name: fileName,
        id: fileId,
      });
    }
  };

  useEffect(() => {
    if (!enable && docPagesMeta) {
      disableInput();
      return;
    }
  }, [enable, docPagesMeta]);

  useEffect(() => {
    if (calculatedSections && activeWidgetId) {
      setTableConfigData();
      setDiscountOptions();
      let tableSelector = document.querySelector(`[data-id="${activeWidgetId}"]`);
      let tableConfig = tableSelector.getAttribute("data-table-config");
      tableConfig = JSON.parse(tableConfig);

      // format all currency
      let grandsubtotal = formattedCurrency(grandSubtotal);
      let onlelinedisamount = formattedCurrency(oneLineDiscountAmount);
      let detailstaxes = detailedTaxes.map(item => ({
        name: item.name,
        amount: formattedCurrency(item.amount),
      })); // Create new array with formatted amounts
      let grandtotal = formattedCurrency(grandTotal);

      // update new json with total, subtotal, sectioon total, tax etc
      let newObj = { ...tableConfig };
      newObj.sections = calculatedSections;
      newObj.subTotal = grandsubtotal;
      newObj.oneLineDiscountAmount = onlelinedisamount;
      newObj.detailedTaxes = detailstaxes;
      newObj.grandTotal = grandtotal;
      const jsonString = JSON.stringify(newObj);

      // update html
      if (targetSection) {
        let target = targetSection.querySelector(".section-total")
          ? targetSection.querySelector(".section-total")
          : targetSection.closest(".card-header").querySelector(".section-total");
        target.innerHTML = formattedCurrency(calculatedSections[targetSectionIndex].total);
      }
      if (targetItem) {
        targetItem.innerHTML = formattedCurrency(calculatedSections[targetSectionIndex].items[targetItemIndex].amount);
      }
      if (tableSelector.querySelector(".grand-discount"))
        tableSelector.querySelector(".grand-discount").innerHTML = onlelinedisamount;
      if (tableSelector?.querySelector("#pricing-table-taxes")) createTaxAreaHtml(detailstaxes);
      if (tableSelector.querySelector(".grand-subtotal"))
        tableSelector.querySelector(".grand-subtotal").innerHTML = grandsubtotal;
      if (tableSelector.querySelector(".grand-total"))
        tableSelector.querySelector(".grand-total").innerHTML = grandtotal;

      // update context
      let targetWidgetIndex = docPagesMeta.widgets.findIndex(widget => widget.id === activeWidgetId);
      let newArray = Object.assign([...docPagesMeta.widgets]);
      newArray = Object.assign([...newArray], {
        [targetWidgetIndex]: {
          ...newArray[targetWidgetIndex],
          style: {
            ...newArray[targetWidgetIndex].style,
            height: document.querySelector(`[data-id="${activeWidgetId}"]`).style.height,
          },
          data: {
            ...newArray[targetWidgetIndex].data,
            "data-table-config": jsonString,
          },
          innerHTML: document.querySelector(`[data-id="${activeWidgetId}"]`).innerHTML,
        },
      });
      setDocPagesMeta({ ...docPagesMeta, widgets: newArray });
      setWidgetDelta(JSON.stringify(newArray.filter(rec => updatedTables.includes(rec.id))));
      setTargetItem();
      setTargetItemIndex();
    }
  }, [calculatedSections, grandSubtotal, oneLineDiscountAmount, detailedTaxes, grandTotal]);

  useEffect(() => {
    if (companyId && enable) {
      dispatch(fetchProposalCurrencyList(companyId)).then(resp => {
        if (resp?.data?.data) {
          setCurrencyInfo(resp.data.data);
        }
      });
    }
  }, [companyId, enable]);

  useEffect(() => {
    // for Itinerary & Pricing Table Images
    const imageElements = document.querySelectorAll(".item-image, .itinerary-image");
    imageElements.forEach(el => {
      el.addEventListener("click", handleImageClick);
    });

    // for Itinerary
    const docAddress = document.querySelectorAll("[data-address], [data-phone-href], [data-url], [data-asset-url]");
    docAddress.forEach(el => {
      el.addEventListener("click", handleWidgetAction);
    });

    if (!enable) return;

    const checkBoxElem = document.querySelectorAll(".custom-checkbox");
    checkBoxElem.forEach(el => {
      el.addEventListener("click", handleOptionClick);
    });

    const radioButtonElem = document.querySelectorAll(".custom-radio");
    radioButtonElem.forEach(el => {
      el.addEventListener("click", handleOptionClick);
    });

    const incQuantity = document.querySelectorAll(".btn-increment");
    incQuantity.forEach(el => {
      el.addEventListener("click", handleQuantityInc);
    });

    const decQuantity = document.querySelectorAll(".btn-decrement");
    decQuantity.forEach(el => {
      el.addEventListener("click", handleQuantityDec);
    });

    // Cleanup on unmount
    return () => {
      imageElements.forEach(el => {
        el.removeEventListener("click", handleImageClick);
      });
      checkBoxElem.forEach(el => {
        el.removeEventListener("click", handleOptionClick);
      });
      radioButtonElem.forEach(el => {
        el.removeEventListener("click", handleOptionClick);
      });
      incQuantity.forEach(el => {
        el.removeEventListener("click", handleQuantityInc);
      });
      decQuantity.forEach(el => {
        el.removeEventListener("click", handleQuantityDec);
      });
      docAddress.forEach(el => {
        el.removeEventListener("click", handleWidgetAction);
      });
    };
  }, [docPagesMeta]);

  return {
    showPricingTableImageCarousel,
    setShowPricingTableImageCarousel,
    widgetDelta,
    activeWidgetType,
  };
};

export default useEstimatePreview;
